import { createContext, useContext, useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("sw_token"));
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);

  const authHeaders = useCallback(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const fetchCartCount = useCallback(async () => {
    if (!token) { setCartCount(0); return; }
    try {
      const res = await fetch(`${API}/api/cart/count`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCartCount(data.count);
      }
    } catch {}
  }, [token, authHeaders]);

  useEffect(() => {
    const init = async () => {
      if (!token) { setLoading(false); return; }
      try {
        const res = await fetch(`${API}/api/auth/me`, { headers: authHeaders() });
        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
          await fetchCartCount();
        } else {
          localStorage.removeItem("sw_token");
          setToken(null);
        }
      } catch {
        localStorage.removeItem("sw_token");
        setToken(null);
      }
      setLoading(false);
    };
    init();
  }, [token, authHeaders, fetchCartCount]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Login failed");
    localStorage.setItem("sw_token", data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (name, email, password) => {
    const res = await fetch(`${API}/api/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Registration failed");
    localStorage.setItem("sw_token", data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("sw_token");
    setToken(null);
    setUser(null);
    setCartCount(0);
  };

  const value = {
    user, token, loading, cartCount, setCartCount,
    login, register, logout, authHeaders, fetchCartCount,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
